import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { QueryClient } from '@tanstack/react-query'
import type { GetRowIdParams, GridReadyEvent, IServerSideGetRowsParams, RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ChangeEvent, ReactElement } from 'react'
import type { IApplicationVersionForListing } from '~/types/application-version'
import type { DeviceType } from '~/types/device-type'

import columns from './columns'
import PanelContent from './PanelContent'

import { getApplicationVersions } from '~/api/application-versions'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import Panel from '~/components/Panel'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useAppsManagementDetailsQuery } from '~/queries/application-management'
import { keys } from '~/queries/versions'
import { getExternalStoreId, transformByDeviceType } from '~/utils/application'
import { debounce } from '~/utils/function'

const ENTRIES_NAME = 'versions'

const SEARCH_FIELD = 'semantic_version'

const CACHE_BLOCK_SIZE = 20

const getDatasource = ({
  deviceType,
  externalId,
  queryClient,
  search,
}: {
  deviceType: DeviceType
  externalId: string
  queryClient: QueryClient
  search: string
}) => ({
  getRows: async (params: IServerSideGetRowsParams) => {
    const { fail, request, success } = params

    try {
      const currentPage = (request.startRow || 0) / CACHE_BLOCK_SIZE

      const queryParams = {
        orderBy: request.sortModel.length ? `${request.sortModel[0].colId}+${request.sortModel[0].sort}` : undefined,
        page: currentPage,
        q: search,
        size: CACHE_BLOCK_SIZE,
      }

      const apiCallParams = {
        deviceType,
        externalId,
        ...queryParams,
      }

      if (apiCallParams) {
        const { data } = await queryClient.fetchQuery({
          queryFn: () => getApplicationVersions(apiCallParams),
          queryKey: keys.listV2(apiCallParams),
        })
        success({ rowData: data || [] })
      }
    } catch (error) {
      captureException(error)
      fail()
    }
  },
})

const VersionsLayout = (): ReactElement => {
  const { appDetails, appKey, deviceType } = useAppSummary()

  const { data: app, isLoading: isAppLoading } = useAppsManagementDetailsQuery(appKey)

  const { searchParamsObject } = useSearchParamsContext()

  const externalId = searchParamsObject.externalId || (app ? getExternalStoreId(app, deviceType) : undefined)

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="versions-view" withPadding>
        <ViewTitle className="layout-view-title">App versions</ViewTitle>
        <ContentLayout loading={!appDetails || !externalId}>
          {isAppLoading ? (
            <MwMLoader category="item" />
          ) : externalId ? (
            <Versions deviceType={deviceType} externalId={externalId} />
          ) : (
            'Application not found.'
          )}
        </ContentLayout>
      </Layout>
    </>
  )
}

const Versions = ({ deviceType, externalId }: { deviceType: DeviceType; externalId: string }): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { version: pathParamVersion } = useParams<{ version?: string }>()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [panelRef, setPanelRef] = useState<HTMLDivElement>()

  const [selectedVersion, setSelectedVersion] = useState<IApplicationVersionForListing>()

  const debouncedSearch = debounce((search) => {
    const datasource = getDatasource({
      deviceType,
      externalId,
      queryClient,
      search,
    })

    gridRef.current?.api?.setGridOption('serverSideDatasource', datasource)
  }, 500)

  /** Handlers */

  const handleRowClicked = (evt: RowClickedEvent) => {
    evt.node.setSelected(true)
    navigate(`/versions/${evt.data.semantic_version}`, { replace: true })
  }

  const handleGetRowId = useCallback(
    ({ data }: GetRowIdParams<IApplicationVersionForListing>) => data.semantic_version,
    []
  )

  const contentRefCallback = (node: HTMLDivElement) => setPanelRef(node)

  const handleClosePanel = useCallback(() => {
    gridRef.current?.api?.deselectAll()
    setSelectedVersion(undefined)
    navigate('/versions', { replace: true })
  }, [navigate])

  const handleSearchChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => debouncedSearch(evt.currentTarget.value),
    [debouncedSearch]
  )

  const handleGridReady = useCallback(
    (evt: GridReadyEvent) => {
      if (externalId && gridRef.current?.api) {
        const datasource = getDatasource({
          deviceType,
          externalId,
          queryClient,
          search: '',
        })

        evt.api.setGridOption('serverSideDatasource', datasource)
      }
    },
    [deviceType, externalId, queryClient]
  )

  /** Effects */

  useDocumentTitle('Versions')

  // NOTE: handle appSummary change to update datasource
  useEffect(() => {
    if (gridRef.current?.api) {
      const datasource = getDatasource({
        deviceType,
        externalId,
        queryClient,
        search: '',
      })

      gridRef.current.api.setGridOption('serverSideDatasource', datasource)
    }
  }, [deviceType, externalId, queryClient])

  // NOTE: handle click outside panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (panelRef &&
          tableRef.current &&
          (tableRef.current.contains(event.target as Node) || panelRef.contains(event.target as Node))) ||
        !document.getElementById('root')?.contains(event.target as Node)
      ) {
        return
      }

      handleClosePanel()
    }

    gridRef.current?.api?.sizeColumnsToFit()
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [gridRef, handleClosePanel, panelRef, tableRef])

  // NOTE: select version from path param
  useEffect(() => {
    if (pathParamVersion) {
      gridRef.current?.api?.forEachNode((node) => {
        if (node.data?.semantic_version === pathParamVersion) {
          node.setSelected(true)
          setSelectedVersion(node.data)
        }
      })
    }
  }, [pathParamVersion])

  /** Render */

  return (
    <>
      <ServerSideTableWrapper
        cacheBlockSize={CACHE_BLOCK_SIZE}
        className="versions-table with-min-height"
        columnDefs={columns}
        entriesName={ENTRIES_NAME}
        getRowId={handleGetRowId}
        onGridReady={handleGridReady}
        onRowClicked={handleRowClicked}
        onSearchChange={handleSearchChange}
        ref={gridRef}
        rowModelType="serverSide"
        rowSelection={{
          checkboxes: false,
          enableClickSelection: true,
          mode: 'singleRow',
        }}
        searchField={SEARCH_FIELD}
        suppressCsvExport
        suppressExcelExport
        tableRef={tableRef}
        withPagination={false}
        withSearchBar
      />
      <Panel
        className="version-panel"
        isOpen={!!pathParamVersion}
        onClosePanel={handleClosePanel}
        panelRef={contentRefCallback}
      >
        {selectedVersion ? (
          <PanelContent
            deviceType={deviceType}
            externalId={externalId}
            locales={selectedVersion.locales}
            semanticVersion={selectedVersion.semantic_version}
          />
        ) : null}
      </Panel>
    </>
  )
}

export default VersionsLayout
